import React, { useCallback } from 'react';
import cn from 'classnames';
import { Icon } from 'ht-styleguide';
/* Utils */
import { scheduledFormat, DATE_FORMAT } from '@utils/date';
import { prevented } from '@utils/event';
/* Hooks */
import useReadMore from '@hooks/useReadMore';
/* Styles */
import { Review as ReviewType } from 'types/reviews.types';
import styles from './review.module.scss';
/* Types */
import StarRating from '../StarRating';

export type ReviewProps = {
  review: ReviewType;
  className?: string;
  suppressAddressLine?: boolean;
  addShadow?: boolean;
};

const Review = ({ addShadow, review, className, suppressAddressLine }: ReviewProps) => {
  const [{ text, collapsed, couldReadMore }, setCollapsed] = useReadMore(review.comment);
  const readMoreText = collapsed ? 'show more' : 'show less';
  const date = review.timestamp;
  const onShowMoreClick = useCallback(() => {
    setCollapsed(!collapsed);
    return true;
  }, [collapsed, setCollapsed]);

  const componentStyle = cn(className, styles.review, {
    [styles.review__shadow]: addShadow,
  });

  return (
    <div className={componentStyle}>
      <div className={styles.head}>
        <StarRating stars={review.stars} size="md" />
        <div className="p2 n700">{scheduledFormat(date, { format: DATE_FORMAT.DATE })}</div>
      </div>

      <div className={cn('p1', styles.text)}>
        {text}
        {couldReadMore && <a href="#" onClick={prevented(onShowMoreClick)}>{` ${readMoreText}`}</a>}
      </div>

      <div className={styles.info}>
        <div>
          <h5 className="h5">{review.author}</h5>
          {!suppressAddressLine && <div className="caption n700">{review.address}</div>}
        </div>

        <h6 className={cn('h6', styles.verified)}>
          Verified
          <Icon name="v2-green-checkmark" className={styles.check} />
        </h6>
      </div>
    </div>
  );
};

export default React.memo(Review);
